import type { Address } from '@rechargeapps/storefront-client'
import { useEffect, useState } from 'react'
import Link from 'next/link'

import type {
	ShopifyBasicOrder,
	ShopifyBasicOrderShippingAddress
} from '@lib/queries/orders/get-basic-orders-query'
import type { ShopifyOrderType } from '@lib/queries/orders/get-order'
import useCustomizationLogic from '@lib/subscription-box/use-customization-logic'

import profileTextNodes from '@data/i18n/profile.json'
import Image from 'next/image'

type Props = {
	shippingInfo: Address | ShopifyBasicOrderShippingAddress
	chargeShopifyOrder: ShopifyBasicOrder | null
	setIsModalOpen: (customizationType: 'NMC' | 'RMC' | null) => void
	numberOrders: number
	productName: string
	subscriptionId: number
	onReactivate: () => Promise<void>
	index: number
}

// TODO: define statuses based on tags and processedAt dates
export default function InactiveSubscriptionItem({
	shippingInfo,
	chargeShopifyOrder,
	setIsModalOpen,
	numberOrders,
	productName,
	subscriptionId,
	onReactivate,
	index
}: Props): React.ReactElement {
	const [isReactivating, setIsReactivating] = useState(false)

	const handleReactivate = async () => {
		try {
			setIsReactivating(true)
			await onReactivate()
		} catch (error) {
			console.error('Failed to reactivate subscription:', error)
		} finally {
			setIsReactivating(false)
		}
	}

	return (
		<li className="border-t border-gray-200 pt-6">
			{/* Top row with name and manage */}
			<div className="flex items-center justify-between mb-4">
				<p className="text-lg font-medium">{productName}</p>
				<Link href={`/profile/subscriptions/${subscriptionId}`}>
					<span className="underline cursor-pointer">
						{profileTextNodes.subscriptionItem.manage}
					</span>
				</Link>
			</div>

			{/* Description and customize button row */}
			<div className="flex max-md:flex-col items-start md:items-center justify-between mb-6 gap-4">
				<address className="not-italic">
					<p className="">
						{(shippingInfo as Address)?.first_name ||
							(shippingInfo as ShopifyBasicOrderShippingAddress)?.firstName}{' '}
						{(shippingInfo as Address)?.last_name ||
							(shippingInfo as ShopifyBasicOrderShippingAddress)?.lastName}
					</p>
					<p className="text-gray-600">
						{shippingInfo?.address1}
						{shippingInfo?.address2 ? `, ${shippingInfo?.address2}` : ''}
					</p>
					<p className="text-gray-600">
						{shippingInfo?.city}, {shippingInfo?.province}, {shippingInfo?.zip}
					</p>
				</address>

				<button
					type="button"
					onClick={handleReactivate}
					disabled={isReactivating}
					className="button button-secondary text-sm bg-bbx-coral hover:bg-bbx-coral-dark max-md:w-full"
				>
					{isReactivating ? 'Reactivating...' : 'Reactivate'}
				</button>
			</div>
		</li>
	)
}
